/**
 * Candidate selection for the AI Scanner results step (STORY-013, #736).
 *
 * STORY-013 shipped this as a seam, not an algorithm: the results UI landed
 * ahead of the Q1/Q2 scoring, so it took the head of the filtered set in its
 * existing order. #736 replaces the body with the real composite-score
 * ranking, which is why the seam existed — `ScanResults` and the summarize
 * payload call the same function they always did.
 *
 * The ranking itself lives in `scanRanking`; this module is the selection and
 * the output shape around it.
 */

import { getAnswerScans } from '../constants/scoringScans';
import { formatScanValue, scanLabel } from './scanDisplayValues';
import { rankPool } from './scanRanking';

/**
 * How many candidates the results step shows. Requirements allow 3-5 (NFR 13,
 * never 1); with real ranking behind the seam the maximum is the right pick —
 * the placeholder took the minimum only because an unranked head of the list
 * had no claim to being the best five.
 *
 * Equal to `MAX_SUMMARY_SYMBOLS` (summarizePayload) by design, not by
 * coincidence: that is Syphon's own `symbols[]` cap, and both bounds trace
 * back to NFR 13's maximum of 5. Raising this past 5 means raising a cap
 * Syphon enforces, so the two move together or not at all.
 *
 * Kept a named constant alongside `DEFAULT_AXIS_WEIGHTS` so the display cap
 * and the axis weights are both tunable without a rewrite.
 */
export const CANDIDATE_COUNT = 5;

/**
 * Width of a Syft industry code, and therefore of every key in the
 * `symbol.industry.*` string table — all 34 of them are exactly four digits,
 * `0050` through `9999`. Exported so a test can assert the table still agrees
 * with it rather than trusting this comment.
 */
export const INDUSTRY_CODE_LENGTH = 4;

/**
 * Builds a candidate's matched scan values — the `{ scan, label, value }`
 * entries #719's summary input expects.
 *
 * These are the TWO ranking scans, in Q1-then-Q2 order: they are precisely
 * what put the symbol where it is, so they are what a "why this stock?"
 * explanation has to cite. The hashtag scans that explain why it is in the
 * pool at all are added separately by `buildSummarizeRequest`.
 *
 * An axis with no value, no label, or no resolved scan is OMITTED rather than
 * sent as `null`/`0`/`"N/A"` — the same rule `matched[]` follows everywhere
 * (AC C19978731). A symbol can therefore carry two entries, one, or none;
 * `matched` being short never blocks its selection, because ranking has
 * already decided that and a missing value has already cost it there.
 */
function buildMatched(symbol, axes, localize) {
  return ['q1', 'q2']
    .map((axisId) => {
      const axis = axes[axisId];
      if (!axis || !axis.scan) return null;
      const value = formatScanValue(axis.scan, axis.value, localize, true);
      if (!value) return null;
      const label = scanLabel(axis.scan, localize);
      if (!label) return null;
      return { scan: axis.scan, label: label, value: value };
    })
    .filter(Boolean);
}

/**
 * Resolves a symbol's display name from the store's symbol metadata.
 *
 * `state.symbols.data[id]` wraps the payload in `.data`; the unwrapped shape
 * is accepted too so tests (and `buildSearchRound`'s already-resolved names)
 * can pass a plain map.
 *
 * Falls back to the symbol ID rather than dropping the candidate: ranking has
 * already earned it a place, and a card is more useful with a ticker on it
 * than absent. (The summarize payload makes the opposite call and skips it —
 * Syphon 400s on a blank name, so there a nameless symbol cannot be sent at
 * all.)
 */
function resolveCompanyName(symbolId, symbolMetadata) {
  const meta = unwrapMetadata(symbolId, symbolMetadata);
  // `?.` rather than `meta && meta.x`: the `&&` form evaluates to `meta` when
  // `meta` is falsy-but-not-nullish, so a `0` or `''` would flow on as the
  // name instead of stopping here. `unwrapMetadata` happens to rule that out
  // today, but that is its invariant to keep, not this function's to rely on.
  //
  // `||` between the two fields, not `??`: an empty `jpShortName` should fall
  // through to `name`, which `??` would not do.
  const name = meta?.jpShortName || meta?.name;
  const trimmed = typeof name === 'string' ? name.trim() : '';
  return trimmed || symbolId;
}

/**
 * Unwraps `state.symbols.data[id]`, which puts the payload under `.data`,
 * accepting the already-unwrapped shape too (see {@link resolveCompanyName}).
 */
function unwrapMetadata(symbolId, symbolMetadata) {
  const entry = (symbolMetadata || {})[symbolId];
  return (entry && entry.data) || entry || null;
}

/**
 * Resolves a symbol's Syft industry CODE — e.g. `'6100'`, not a label.
 *
 * STORY-024 (#734): the results card shows the industry as a tag, and only
 * this seam reads `state.symbols.data` (#736), so the code travels on the
 * candidate the same way the name does. Localizing it is the view's job —
 * `ScanResults` resolves the `symbol.industry.*` label and drops a code the
 * table does not know.
 *
 * Numeric codes are stringified so the candidate shape is a stable
 * `?string`: Syft sends `'3700'`, but some store fixtures carry `3700`.
 * Anything else (missing, empty, non-scalar) becomes `null` — an absent
 * industry is an empty slot on the card, never a guess.
 *
 * Stringifying a number has to RE-PAD it to the table's width. Every key in
 * the `symbol.industry.*` table is exactly {@link INDUSTRY_CODE_LENGTH}
 * digits, and one of them leads with a zero — `0050`
 * (水産・農林業). A number cannot carry that zero, so a bare `String(50)`
 * yields `'50'`, misses the table, and the card loses its tag with no error
 * anywhere. `padStart` is a no-op for the other 33 codes and for the `9999`
 * unknown sentinel.
 */
function resolveIndustryCode(symbolId, symbolMetadata) {
  const meta = unwrapMetadata(symbolId, symbolMetadata);
  // `?.`, for the reason given in `resolveCompanyName` above.
  const code = meta?.jpIndustry;
  if (typeof code === 'number' && Number.isFinite(code)) {
    return String(code).padStart(INDUSTRY_CODE_LENGTH, '0');
  }
  const trimmed = typeof code === 'string' ? code.trim() : '';
  return trimmed || null;
}

/**
 * Resolves a symbol's Syft company `description` (opt-in via
 * `descriptions=true`). Syft omits the field when it has none, so anything
 * that is not a non-blank string becomes `''` — the card's placeholder case.
 * Named `shortDescription` on the candidate so live and restored results share
 * one shape.
 */
function resolveDescription(symbolId, symbolMetadata) {
  const description = unwrapMetadata(symbolId, symbolMetadata)?.description;
  return typeof description === 'string' ? description.trim() : '';
}

/**
 * Ranks the filtered pool and selects the best candidates.
 *
 * Deterministic: the same pool, answers and scan data always produce the same
 * ordered candidates. Ties keep the pool's incoming order — no `MarketCap`
 * tie-break.
 *
 * Called with no options it degrades to the pool's own order, which is what
 * keeps it safe to call before both questions are answered.
 *
 * @param {string[]} [pool] - Symbol IDs from the hashtag filter, in the order
 *   the filter produced them.
 * @param {object} [options]
 * @param {{q1: ?string, q2: ?string}} [options.answers] - Q1/Q2 option codes.
 * @param {object} [options.scanData] - `state.scans.scanData`.
 * @param {object} [options.symbolMetadata] - `state.symbols.data`.
 * @param {Function} [options.localize] - `useLocalize()`'s localize; without
 *   it `matched` comes back empty, since a label cannot be resolved.
 * @param {{q1: number, q2: number}} [options.weights] - Axis weights.
 * @param {number} [options.count] - Cap; defaults to {@link CANDIDATE_COUNT}.
 * @returns {Array<{symbol: string, companyName: string, jpIndustry: ?string, shortDescription: string, matched: Array<{scan: string, label: string, value: string}>, composite: number, axes: object}>}
 *   Up to `count` candidates in ranked order, best first. Never the input
 *   array, and never mutates it.
 */
export const selectCandidates = (pool, options) => {
  if (!Array.isArray(pool)) return [];

  const {
    answers = null,
    scanData = null,
    symbolMetadata = null,
    localize = null,
    weights = undefined,
    count = CANDIDATE_COUNT
  } = options || {};

  const limit = Number.isInteger(count) && count >= 0 ? count : CANDIDATE_COUNT;

  return rankPool(pool, answers, scanData, weights)
    .slice(0, limit)
    .map(({ symbol, composite, axes }) => ({
      symbol: symbol,
      companyName: resolveCompanyName(symbol, symbolMetadata),
      jpIndustry: resolveIndustryCode(symbol, symbolMetadata),
      shortDescription: resolveDescription(symbol, symbolMetadata),
      matched: buildMatched(symbol, axes, localize),
      composite: composite,
      axes: axes
    }));
};

/**
 * The ranked symbol IDs alone — the shape callers that only need an ordering
 * want (name prefetching, the summarize request's `symbols[]`).
 *
 * A thin wrapper rather than a second code path, so an ID list and a candidate
 * list can never disagree about who ranked where.
 *
 * @param {string[]} [pool]
 * @param {object} [options] - As {@link selectCandidates}.
 * @returns {string[]}
 */
export const selectCandidateSymbols = (pool, options) =>
  selectCandidates(pool, options).map(({ symbol }) => symbol);

/**
 * Fills each persisted result's empty `shortDescription`/`jpIndustry` from the
 * entry for the same symbol in `source`, without overwriting a value already
 * set. Symbol metadata can land after a round is saved, so this lets the saved
 * copy catch up.
 *
 * @param {Array<object>} results - A `SearchRound.results` array.
 * @param {Array<object>} [source] - Candidates or results to fill from.
 * @returns {Array<object>} `results` itself when nothing was filled.
 */
export const fillMissingResultMetadata = (results, source) => {
  if (!Array.isArray(results) || !Array.isArray(source)) return results;
  const bySymbol = new Map(source.map((entry) => [entry.symbol, entry]));
  let changed = false;
  const filled = results.map((result) => {
    const match = bySymbol.get(result.symbol);
    if (!match) return result;
    const shortDescription = result.shortDescription || match.shortDescription || '';
    const jpIndustry = result.jpIndustry || match.jpIndustry || null;
    if (
      shortDescription === (result.shortDescription || '') &&
      jpIndustry === (result.jpIndustry || null)
    ) {
      return result;
    }
    changed = true;
    return { ...result, shortDescription, jpIndustry };
  });
  return changed ? filled : results;
};

/**
 * The scans that rank a given answer pair, de-duplicated and in Q1-then-Q2
 * order. Re-exported through the seam so a caller assembling a payload does
 * not need to know the mapping module exists.
 *
 * @param {{q1: ?string, q2: ?string}} [answers]
 * @returns {string[]}
 */
export const getRankingScans = (answers) => {
  const scans = getAnswerScans(answers);
  return Array.from(
    new Set([scans.q1, scans.q2].filter((scan) => scan !== null))
  );
};

export default selectCandidates;
